import type {
  ComponentPropsWithRef,
  ComponentType,
  KeyboardEvent,
} from 'react';
import { NumericFormat } from 'react-number-format';
import type { NumericFormatProps } from 'react-number-format';

type Props = Omit<
  ComponentPropsWithRef<typeof NumericFormat>,
  'decimalSeparator'
> & {
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
};

// We're using a custom input component to intercept the input value onChange
// This allows a user to enter either `.` or `,` as a decimal separator
// However, a `,` decimal separator will always be converted to a `.`
const CustomInput: ComponentType = ({
  defaultValue,
  value,
  onChange,
  onFocus,
  ...rest
}: NumericFormatProps) => (
  <input
    value={String(defaultValue ?? value)}
    onChange={(e) => {
      const { value } = e.target;
      e.target.value = value.replace(',', '.');
      // Now let the NumericFormat component handle the rest
      onChange?.(e);
    }}
    {...rest}
  />
);

export const NumericInput = ({ value, ...rest }: Props) => {
  return (
    <NumericFormat
      type="text"
      inputMode="decimal"
      decimalSeparator="." // This component is opionated and using a `.` as a decimal separator. The custom input component will convert `,` to `.`
      allowNegative={false}
      isAllowed={(values) => {
        const { floatValue } = values;
        if (typeof floatValue === 'undefined') return true;
        // Number.MAX_SAFE_INTEGER is the maximum safe integer in JavaScript
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/MAX_SAFE_INTEGER
        // For example, Number.MAX_SAFE_INTEGER + 1 === Number.MAX_SAFE_INTEGER + 2 will evaluate to true, which is mathematically incorrect
        return floatValue <= Number.MAX_SAFE_INTEGER;
      }}
      value={value ?? ''}
      {...rest}
      customInput={CustomInput}
    />
  );
};
